import type {
    CabinetCall,
    CabinetEmail,
    CabinetLetter,
    CabinetMail,
    CabinetReceivedFax,
    CabinetSentFax,
    FileCabinetDataSource,
    FileCabinetListKey,
    FileCabinetRecord,
    FileCabinetViewKey,
} from './file-cabinet.types';
import { fileCabinetRecordId, FileCabinetRequestError } from './file-cabinet.types';
import { FILE_CABINET_OTHER_CALL_METHODS } from './file-cabinet.views';

/**
 * Which mail, letters, faxes, emails and calls carry a document — the drawer's "Referenced by" and
 * the table's "Attached to". The API has no single call for it: each list takes `filterDocumentId`,
 * so it is one request per list (design: `API Coverage.md`, "one call per endpoint").
 *
 * AI calls are not asked: `/phone/ai?filterDocumentId` answers 500 (sandbox, 2026-10-22), and an AI
 * call record carries no document anyway.
 */

export interface FileCabinetReference {
    view: FileCabinetViewKey;
    id: string;
    record: FileCabinetRecord;
    /**
     * False for a call whose method none of the three call tables lists (no method, or one outside the
     * enum): it is shown in a drawer, but there is no table to switch to.
     */
    listed?: boolean;
}

/**
 * The lists asked, one request each. Calls are asked once — `call.all`, every method — and each call is
 * placed in its table afterwards (`fileCabinetCallView`): three requests would ask the same endpoint.
 */
export const FILE_CABINET_REFERENCE_LISTS: FileCabinetListKey[] = ['mail.in', 'mail.out', 'fax.in', 'fax.out', 'email.out', 'call.all'];

/** How each list is named when it could not be checked. */
export const FILE_CABINET_REFERENCE_LIST_NAMES: Partial<Record<FileCabinetListKey, string>> = {
    'mail.in': 'received mail',
    'mail.out': 'letters',
    'fax.in': 'received faxes',
    'fax.out': 'sent faxes',
    'email.out': 'emails',
    'call.all': 'calls',
};

/**
 * The lists one endpoint serves. A 403 is about the endpoint, so on any call list it is about all four:
 * the three tables and `call.all` read the same `/phone`.
 */
export function fileCabinetSameEndpoint(list: FileCabinetListKey): FileCabinetListKey[] {
    return list === 'call.in' || list === 'call.out' || list === 'call.other' || list === 'call.all'
        ? ['call.in', 'call.out', 'call.other', 'call.all']
        : [list];
}

/**
 * The table a call is listed in, by its method — undefined when none lists it: "Other methods" asks
 * for six named methods, so a call with no method, or one outside the enum, is in no table.
 */
export function fileCabinetCallView(call: CabinetCall): FileCabinetViewKey | undefined {
    if (call.method === 'INBOUND') {
        return 'call.in';
    }
    if (call.method === 'OUTBOUND') {
        return 'call.out';
    }
    return call.method && FILE_CABINET_OTHER_CALL_METHODS.includes(call.method) ? 'call.other' : undefined;
}

/** The document a mail, letter, fax, email or call record carries. */
export function fileCabinetCarriedDocumentId(view: FileCabinetViewKey, record: FileCabinetRecord): string | undefined {
    switch (view) {
        case 'mail.in':
        case 'mail.out':
        case 'fax.in':
        case 'fax.out': {
            const carrier = record as CabinetMail | CabinetLetter | CabinetReceivedFax | CabinetSentFax;
            return carrier.document?.id ?? carrier.documentId;
        }
        case 'email.out':
            return (record as CabinetEmail).document?.id;
        case 'call.in':
        case 'call.out':
        case 'call.other':
            return (record as CabinetCall).document?.id;
        default:
            return undefined;
    }
}

/** The records one list returned, as references keyed by the document each carries. */
export function fileCabinetReferencesFrom(
    list: FileCabinetListKey,
    records: FileCabinetRecord[],
): { documentId: string; reference: FileCabinetReference }[] {
    const out: { documentId: string; reference: FileCabinetReference }[] = [];
    for (const record of records) {
        if (list === 'call.all') {
            // Every method: place each call in the table that lists it.
            const placed = fileCabinetCallView(record as CabinetCall);
            const view = placed ?? 'call.other';
            const documentId = fileCabinetCarriedDocumentId(view, record);
            const id = fileCabinetRecordId(view, record);
            if (documentId && id) {
                out.push({ documentId, reference: { view, id, record, listed: placed !== undefined } });
            }
            continue;
        }
        const documentId = fileCabinetCarriedDocumentId(list, record);
        const id = fileCabinetRecordId(list, record);
        if (documentId && id) {
            out.push({ documentId, reference: { view: list, id, record } });
        }
    }
    return out;
}

/** What carries a set of documents, as far as the lists could tell. */
export interface FileCabinetReferences {
    byDocument: Record<string, FileCabinetReference[]>;
    /** Lists not read: 403 (`forbidden`, asked now or known from earlier), or failed. */
    unchecked: { list: FileCabinetListKey; forbidden: boolean }[];
    /** A list had more matches than one page: there may be more references than these. */
    truncated: boolean;
}

/**
 * Every mail, letter, fax, email and call that carries one of these documents — one request per list,
 * every document ORed into it. The one loader behind "Attached to" (a page of documents) and
 * "Referenced by" (one document).
 *
 * - Only records that carry one of THESE documents are kept: `filterDocumentId` is measured on letters
 *   and sent email (sandbox, 2026-10-22), not on mail, faxes or calls, which had no rows — a list that
 *   ignored it must not make unrelated records look linked (NS-3).
 * - `skip`: lists already known to answer 403 this visit are not asked again — reported as unchecked,
 *   no access, so an empty result still never reads as "none".
 */
export async function fileCabinetLoadReferences(
    source: Pick<FileCabinetDataSource, 'list'>,
    documentIds: string[],
    options: { limit: number; skip?: ReadonlySet<FileCabinetListKey> },
): Promise<FileCabinetReferences> {
    const out: FileCabinetReferences = { byDocument: {}, unchecked: [], truncated: false };
    if (documentIds.length === 0) {
        return out;
    }
    const wanted = new Set(documentIds);
    const asked = FILE_CABINET_REFERENCE_LISTS.filter(list => {
        if (options.skip?.has(list)) {
            out.unchecked.push({ list, forbidden: true });
            return false;
        }
        return true;
    });
    const filters = fileCabinetDocumentIdFilter(documentIds);
    const results = await Promise.allSettled(asked.map(list => source.list(list, { limit: options.limit, offset: 0, filters })));
    results.forEach((result, index) => {
        const list = asked[index];
        if (result.status === 'rejected') {
            out.unchecked.push({ list, forbidden: result.reason instanceof FileCabinetRequestError && result.reason.forbidden });
            return;
        }
        out.truncated = out.truncated || result.value.total > result.value.items.length;
        for (const { documentId, reference } of fileCabinetReferencesFrom(list, result.value.items as FileCabinetRecord[])) {
            if (wanted.has(documentId)) {
                (out.byDocument[documentId] = out.byDocument[documentId] ?? []).push(reference);
            }
        }
    });
    return out;
}

/**
 * `EQ:a,EQ:b,…` — the API ORs comma-joined values (letters: 11 + 2 = 13, sandbox 2026-10-22), so one
 * request per list covers every document on the page.
 */
export function fileCabinetDocumentIdFilter(documentIds: string[]): Record<string, string[]> {
    return { filterDocumentId: [documentIds.map(id => `EQ:${id}`).join(',')] };
}

/**
 * A reference cut down to what names it — the "Attached to" column keeps one per record for a whole
 * page, and needs only the label. The rest (an email's body, a call's agent and recording, a letter's
 * addresses) is PHI it never shows, so it is not kept (minimum necessary). The drawer's "Referenced
 * by" opens its references and keeps them whole.
 */
export function fileCabinetReferenceSummary(reference: FileCabinetReference): FileCabinetReference {
    const record = reference.record as Record<string, unknown>;
    const to = (record.response as { to?: { company?: string; name?: string } } | undefined)?.to;
    const kept: Record<string, unknown> = { id: record.id };
    switch (reference.view) {
        case 'mail.in':
            kept.barcode = record.barcode;
            break;
        case 'mail.out':
            kept.response = to ? { to: { company: to.company, name: to.name } } : undefined;
            break;
        case 'fax.in':
            kept.fromE164 = record.fromE164;
            break;
        case 'fax.out':
            kept.toE164 = record.toE164;
            break;
        case 'email.out':
            kept.subject = record.subject;
            break;
        default:
            kept.method = record.method;
            kept.phoneE164 = record.phoneE164;
    }
    return { ...reference, record: kept as FileCabinetRecord };
}
